/**
 * Чистая логика кнопки «Опубликовать» — общая для рилса и поста.
 *
 * Правило с 02.11.2026: публикуем только в отмеченные аккаунты. Раньше при
 * пустом списке галочек форма молча публиковала в аккаунт, открытый в превью
 * (а превью само выбирает первый аккаунт) — то есть в паблик, который человек
 * не выбирал.
 */

/** Только отмеченные, в порядке списка. Пусто — публиковать некуда. */
export function accountsToPublish<T extends { id: string }>(all: readonly T[], checked: ReadonlySet<string>): T[] {
  return all.filter((a) => checked.has(a.id));
}

/** Чего не хватает до публикации: подпись для человека и id поля, к которому прокрутить. */
export interface MissingField {
  label: string;
  anchor: string;
}

export function missingForReel(s: {
  checkedCount: number;
  hasVideo: boolean;
  hasCategory: boolean;
  needsTextPosition: boolean;
}): MissingField[] {
  const out: MissingField[] = [];
  if (s.checkedCount === 0) out.push({ label: 'аккаунты', anchor: 'publish-accounts' });
  if (!s.hasVideo) out.push({ label: 'видео', anchor: 'publish-media' });
  if (s.needsTextPosition) out.push({ label: 'позиция текста', anchor: 'publish-media' });
  if (!s.hasCategory) out.push({ label: 'тип контента', anchor: 'content-type' });
  return out;
}

export function missingForPost(s: { checkedCount: number; mediaCount: number; hasCategory: boolean }): MissingField[] {
  const out: MissingField[] = [];
  if (s.checkedCount === 0) out.push({ label: 'аккаунты', anchor: 'publish-accounts' });
  if (s.mediaCount === 0) out.push({ label: 'фото или видео', anchor: 'publish-media' });
  if (!s.hasCategory) out.push({ label: 'тип контента', anchor: 'content-type' });
  return out;
}

/** «Не хватает: видео, тип контента» — строка под кнопкой и текст тоста. */
export function missingText(missing: readonly MissingField[]): string {
  return missing.length ? `Не хватает: ${missing.map((m) => m.label).join(', ')}` : '';
}

/** 1 аккаунт, 2 аккаунта, 5 аккаунтов. */
export function accountsWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'аккаунт';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'аккаунта';
  return 'аккаунтов';
}

/** «01.11 18:00» — коротко, год лишний: дальше пары недель не планируют. */
export function formatScheduledShort(at: Date): string {
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${pad(at.getDate())}.${pad(at.getMonth() + 1)} ${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

/** Строка datetime-local или ISO → Date; пусто/мусор → null. */
export function parseScheduled(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
}

/**
 * Подпись кнопки и заголовка окна: «Опубликовать в 3 аккаунта»,
 * «Запланировать на 01.11 18:00 в 3 аккаунта». Без отмеченных — просто глагол.
 */
export function publishActionLabel({ count, scheduledAt }: { count: number; scheduledAt?: Date | null }): string {
  const verb = scheduledAt ? `Запланировать на ${formatScheduledShort(scheduledAt)}` : 'Опубликовать';
  return count > 0 ? `${verb} в ${count} ${accountsWord(count)}` : verb;
}

/**
 * Какие аккаунты отметить у ролика из «Монтажа». С 08.10.2026 одна публикация
 * = один аккаунт: отмечаем аккаунт самого ролика. Аккаунта у ролика нет, а у
 * направления он один — его. Иначе ничего: остальные аккаунты направления стоят
 * первыми в списке, но галочку человек ставит сам.
 */
export function montagePreselect(
  itemAccountId: string | null | undefined,
  projectAccountIds: readonly string[],
  availableIds: ReadonlySet<string>,
): string[] {
  if (itemAccountId && availableIds.has(itemAccountId)) return [itemAccountId];
  if (itemAccountId) return [];
  const ready = projectAccountIds.filter((id) => availableIds.has(id));
  return ready.length === 1 ? ready : [];
}

/** Куда вернуться из формы, открытой из «Монтажа» или «Роликов B2G». */
export function montageReturn(p: {
  montageTask: string | null;
  montageItem: string | null;
  takeItem: string | null;
}): { label: string; to: string } | null {
  if (p.takeItem) return { label: 'Вернуться в Ролики B2G', to: '/takes' };
  if (p.montageTask && p.montageItem) {
    const q = new URLSearchParams({ task: p.montageTask, item: p.montageItem });
    return { label: 'Вернуться в Монтаж', to: `/montage?${q.toString()}` };
  }
  return null;
}

/** Время по умолчанию для «Запланировать»: через час, ровно на пятиминутке. */
export function defaultScheduleAt(now: Date): Date {
  const d = new Date(now.getTime() + 60 * 60_000);
  d.setSeconds(0, 0);
  d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5);
  return d;
}
